<template>
  <div>
    <action-alert
      :close-method="closeAlert"
      :type="alert.type"
      :icon="alert.icon"
      :is-visible="alert.isVisible"
    >{{ alert.text }}</action-alert>

    <div class="container-fluid">
      <Breadcrumb :values="{ Home: '/', 'Bulk Extract': '' }"></Breadcrumb>

      <div class="animated fadeIn">
        <!-- Filter card -->
        <div class="card search-filters-card">
          <div class="card-body search-filters-card-body">
            <div class="description-wrapper">
              <h1 class="page-header">Bulk Raw RDF Extract</h1>
              <p class="description-copy">
                Export respondent-level raw data for one or more studies. Filter by client, category, year, country,
                and/or product type, then toggle which metrics to include.
              </p>
              <p class="description-copy">
                One row per respondent × fragrance, with a column per selected metric. Delivered as XLSX, or CSV if
                the result is too large for Excel.
              </p>
              <p class="description-copy muted-label" v-if="maxExportRows">
                Exports are capped at {{ maxExportRows.toLocaleString() }} rows. Select specific metrics for a
                smaller, faster export.
              </p>
            </div>

            <form class="analyst-form" @submit.prevent="runExtract">
              <div class="row w-100">
                <!-- Client -->
                <div class="col-12 col-xl d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Client</label>
                    <el-select
                      v-model="filters.client"
                      multiple
                      collapse-tags
                      collapse-tags-tooltip
                      filterable
                      clearable
                      placeholder="All clients"
                      fit-input-width
                    >
                      <el-option
                        v-for="opt in clientOptions"
                        :key="opt"
                        :label="opt"
                        :value="opt"
                      ></el-option>
                    </el-select>
                  </div>
                </div>

                <!-- Category -->
                <div class="col-12 col-xl d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Category</label>
                    <el-select
                      v-model="filters.category"
                      multiple
                      collapse-tags
                      collapse-tags-tooltip
                      filterable
                      clearable
                      placeholder="All categories"
                      fit-input-width
                    >
                      <el-option
                        v-for="opt in categoryOptions"
                        :key="opt"
                        :label="opt"
                        :value="opt"
                      ></el-option>
                    </el-select>
                  </div>
                </div>

                <!-- Year -->
                <div class="col-12 col-xl d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Year</label>
                    <el-select
                      v-model="filters.year"
                      multiple
                      collapse-tags
                      collapse-tags-tooltip
                      filterable
                      clearable
                      placeholder="All years"
                      fit-input-width
                    >
                      <el-option
                        v-for="opt in yearOptions"
                        :key="opt"
                        :label="String(opt)"
                        :value="opt"
                      ></el-option>
                    </el-select>
                  </div>
                </div>

                <!-- Country -->
                <div class="col-12 col-xl d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Country</label>
                    <el-select
                      v-model="filters.country"
                      multiple
                      collapse-tags
                      collapse-tags-tooltip
                      filterable
                      clearable
                      placeholder="All countries"
                      fit-input-width
                    >
                      <el-option
                        v-for="opt in countryOptions"
                        :key="opt"
                        :label="opt"
                        :value="opt"
                      ></el-option>
                    </el-select>
                  </div>
                </div>

                <!-- Product Type -->
                <div class="col-12 col-xl d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Product Type</label>
                    <el-select
                      v-model="filters.product_type"
                      multiple
                      collapse-tags
                      collapse-tags-tooltip
                      filterable
                      clearable
                      placeholder="All product types"
                      fit-input-width
                    >
                      <el-option
                        v-for="opt in productTypeOptions"
                        :key="opt"
                        :label="opt"
                        :value="opt"
                      ></el-option>
                    </el-select>
                  </div>
                </div>
              </div>

              <!-- Study count indicator -->
              <div class="row w-100 mt-2" v-if="hasFilter">
                <div class="col-12">
                  <span v-if="countLoading" class="muted-label">
                    <i class="fa fa-spinner fa-spin mr-1"></i> Checking matching studies…
                  </span>
                  <span v-else-if="studyCount !== null" class="muted-label">
                    <i class="fa fa-database mr-1"></i>
                    <strong>{{ studyCount }}</strong> {{ studyCount === 1 ? 'study' : 'studies' }} match the current filters
                    <span v-if="resultsCount !== null && resultsCount !== studyCount"> ({{ resultsCount }} result sets)</span>
                  </span>
                  <span v-else-if="studyCount === 0" class="text-warning small">
                    <i class="fa fa-exclamation-triangle mr-1"></i> No studies match the current filters.
                  </span>
                </div>
              </div>

              <!-- Questions toggle chips -->
              <div class="row w-100 mt-3">
                <div class="col-12">
                  <label class="d-block mb-2">
                    Questions to include
                    <span class="muted-label">(click to toggle; nothing selected = export all)</span>
                    <span class="ml-3">
                      <a href="#" class="chip-action-link" @click.prevent="clearAllQuestions">None</a>
                    </span>
                  </label>

                  <!-- Favorites bar — always visible, greyed when unavailable for current studies -->
                  <div class="chip-group favorites-row">
                    <span class="chip-group-label">Favorites</span>
                    <span
                      v-for="q in favoriteChips"
                      :key="q.label"
                      class="question-chip"
                      :class="{
                        active: isQuestionSelected(q.label),
                        disabled: !q.available
                      }"
                      :title="q.available ? '' : 'Not available for the current study selection'"
                      @click="q.available && toggleQuestion(q.label)"
                    >{{ q.name }}</span>
                    <span
                      v-if="questionsLoading"
                      class="muted-label ml-2"
                    ><i class="fa fa-spinner fa-spin mr-1"></i> checking availability…</span>
                  </div>

                  <!-- Per-group collapsible disclosures, collapsed by default -->
                  <div v-if="questionGroups.length" class="question-chips mt-3">
                    <div
                      v-for="group in questionGroups"
                      :key="group.type"
                      class="chip-group-collapsible"
                    >
                      <button
                        type="button"
                        class="chip-group-toggle"
                        @click="toggleGroup(group.type)"
                      >
                        <i
                          class="fa"
                          :class="isGroupExpanded(group.type) ? 'fa-chevron-down' : 'fa-chevron-right'"
                        ></i>
                        <span class="ml-1">{{ formatGroupLabel(group.type) }}</span>
                        <span class="muted-label ml-2">({{ group.items.length }})</span>
                      </button>
                      <div v-if="isGroupExpanded(group.type)" class="chip-group-body">
                        <span
                          v-for="q in group.items"
                          :key="q.label"
                          class="question-chip"
                          :class="{ active: isQuestionSelected(q.label) }"
                          @click="toggleQuestion(q.label)"
                        >{{ q.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="broadExportWarning" class="row w-100 mt-2">
                <div class="col-12">
                  <span class="text-warning small">
                    <i class="fa fa-exclamation-triangle mr-1"></i>{{ broadExportWarning }}
                  </span>
                </div>
              </div>

              <div class="search-actions mt-3">
                <button
                  class="search-button btn"
                  type="submit"
                  :disabled="isSubmitting || !hasFilter || resultsCount === 0"
                >
                  {{ isSubmitting ? 'Launching…' : 'Run Extract' }}
                </button>
              </div>

              <p v-if="!hasFilter" class="text-muted mt-2 small">
                Please select at least one filter (client, category, year, or country).
              </p>
            </form>
          </div>
        </div>

        <!-- Job history card — newest at top, persisted across reloads -->
        <div v-if="jobs.length" class="card results-card mt-4">
          <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
            <span>Recent Extracts</span>
            <a href="#" class="chip-action-link" @click.prevent="clearHistory">Clear history</a>
          </div>
          <div class="card-body results-body p-0">
            <div
              v-for="j in jobs"
              :key="j.jobId"
              class="job-row d-flex align-items-center px-3 py-2"
            >
              <div class="job-status mr-3">
                <i v-if="j.status === 'pending' || j.status === 'running'"
                   class="fa fa-spinner fa-spin text-muted"></i>
                <i v-else-if="j.status === 'done'" class="fa fa-check-circle text-success"></i>
                <i v-else-if="j.status === 'error'" class="fa fa-times-circle text-danger"></i>
                <i v-else class="fa fa-circle-o text-muted"></i>
              </div>
              <div class="job-meta flex-grow-1">
                <div class="job-name">
                  <strong v-if="j.downloadName">{{ j.downloadName }}</strong>
                  <span v-else class="text-muted">{{ j.jobId.slice(0, 8) }}…</span>
                </div>
                <div class="job-sub muted-label">
                  <span v-if="j.status === 'pending'">queued · polling every {{ pollIntervalSec }}s</span>
                  <span v-else-if="j.status === 'running'">{{ runningStageText(j) }}</span>
                  <span v-else-if="j.status === 'done'">
                    {{ j.rowCount != null ? `${j.rowCount.toLocaleString()} rows` : 'complete' }}
                    <span v-if="j.completedAt"> · {{ formatTimestamp(j.completedAt) }}</span>
                  </span>
                  <span v-else-if="j.status === 'error'" class="text-danger">{{ j.error || 'failed' }}</span>
                </div>
                <div v-if="j.warning" class="text-warning small">⚠ {{ j.warning }}</div>
              </div>
              <div class="job-actions">
                <button
                  v-if="j.status === 'done'"
                  class="btn btn-sm search-button"
                  @click="downloadFile(j.jobId)"
                >
                  <i class="fa fa-download mr-1"></i> Download
                </button>
                <button
                  v-if="j.status === 'done' || j.status === 'error'"
                  class="btn btn-sm btn-link text-muted ml-2"
                  :title="'Remove from history'"
                  @click="removeJob(j.jobId)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import ActionAlert from '../components/shared/ActionAlert'
import Breadcrumb from '../components/shared/Breadcrumb'
import http from '../services/http'

// Favorites are sourced from /bulk-extract/questions → `favorites`. The
// backend resolves each friendly name (e.g. "clean") to its canonical
// label(s) using the same `_attributes` registry the rest of the app
// reads (pkg.symrise.attributes, fed from attributes_regex.json). The Vue
// component never hardcodes a label — it just renders the backend payload
// and greys a chip out when none of its labels appear in availableLabels.

export default {
  name: 'BulkExtract',
  components: { ActionAlert, Breadcrumb },

  data () {
    return {
      filters: {
        // All study filters are multi-select. Empty array = "no constraint".
        client: [],
        category: [],
        year: [],
        country: [],
        product_type: [],
        questions: []
      },
      dropdownOptions: {},
      questionOptions: [],
      favoritesRegistry: [],
      questionsLoading: false,
      questionsLoaded: false,
      questionsDebounce: null,
      // Performance group is the curated "core metrics" disclosure
      // (Liking / Strength / Purchase Intention). Open by default; everything
      // else stays collapsed so the form isn't overwhelming on first paint.
      expandedGroups: { Performance: true },
      studyCount: null,
      resultsCount: null,
      // The backend's whole-export row ceiling (see MAX_TOTAL_EXPORT_ROWS in
      // bulk_extract.py — NOT MAX_FETCH_ROWS, an internal per-chunk safety
      // net that chunked extraction retries past transparently and never
      // surfaces as a failure), surfaced via /count so the UI states the
      // number that actually matters instead of users never being told.
      maxExportRows: null,
      countLoading: false,
      countDebounce: null,
      dropdownDebounce: null,
      // jobs[] is the history (newest-first). Persisted across page reloads
      // via localStorage so users can re-download yesterday's extract.
      jobs: [],
      pollTimers: {},
      pollIntervalSec: 10,
      maxHistory: 20,
      storageKey: 'iris.bulkExtract.jobs.v1',
      isSubmitting: false,
      alert: { type: '', icon: '', text: '', isVisible: false }
    }
  },

  computed: {
    clientOptions () {
      return this.dropdownOptions.client || []
    },

    categoryOptions () {
      return this.dropdownOptions.category || []
    },

    yearOptions () {
      const vals = (this.dropdownOptions.year || []).map(Number).filter(Boolean)
      return vals.sort((a, b) => b - a)
    },

    countryOptions () {
      return this.dropdownOptions.country || []
    },

    productTypeOptions () {
      return this.dropdownOptions.product_type || []
    },

    availableLabels () {
      return new Set(this.questionOptions.map(q => q.label))
    },

    favoriteChips () {
      // Each backend favorite is { name, labels: [variant1, variant2, ...] }.
      // The chip is "available" if any of its labels appears in the ES
      // response. Until the first response lands, default to available so
      // we don't grey everything out on initial render.
      const awaitingFirstResponse = !this.questionsLoaded
      const availableSet = this.availableLabels
      return this.favoritesRegistry.map(fav => {
        const matched = (fav.labels || []).find(l => availableSet.has(l))
        return {
          name: fav.name,
          // The label we actually toggle into filters.questions. Falls back
          // to the first registry label so toggling still does something
          // sensible before ES has responded.
          label: matched || (fav.labels && fav.labels[0]) || fav.name,
          available: awaitingFirstResponse || !!matched
        }
      })
    },

    // Non-favorite questions grouped by type. Performance + each Attribute
    // family lives in its own collapsible disclosure.
    favoriteLabelSet () {
      const all = []
      for (const fav of this.favoritesRegistry) {
        for (const l of (fav.labels || [])) all.push(l)
      }
      return new Set(all)
    },

    questionGroups () {
      const favSet = this.favoriteLabelSet
      const grouped = {}
      for (const q of this.questionOptions) {
        if (favSet.has(q.label)) continue
        if (!grouped[q.type]) grouped[q.type] = []
        grouped[q.type].push(q)
      }
      return Object.entries(grouped).map(([type, items]) => ({ type, items }))
    },

    hasFilter () {
      return !!(
        this.filters.client.length ||
        this.filters.category.length ||
        this.filters.year.length ||
        this.filters.country.length
      )
    },

    // "Nothing selected = export all" is convenient but means a broad study
    // filter with no metric selected pulls every metric x every respondent x
    // every fragrance. Chunked extraction (see backend) now handles this
    // without failing, but it's still real work — several minutes, and
    // likely delivered as CSV (see XLSX_ROW_LIMIT) rather than XLSX. Warn
    // before Run so that's expected rather than surprising.
    //
    // Does NOT display a specific metric count: questionOptions is sourced
    // from Elasticsearch's `questions` index (metrics_options), which only
    // reflects each question's *original* classification. Databricks' own
    // bronze->silver pipeline does further derivation on top of that (CATA/
    // checkbox-question expansion) with no path back to ES — so the real
    // export can include noticeably more distinct metrics than ES's count
    // (confirmed on a real export, 2026-09-24: ES said 170, the export
    // itself resolved 214). Showing a number here would just be a second,
    // contradictable wrong answer next to the real one in the result. Still
    // used internally (isBroad below) just to decide *whether* to warn at
    // all — an undercount there only means the warning shows up less often
    // than ideal, not that it asserts a wrong fact to the user.
    broadExportWarning () {
      if (this.filters.questions.length > 0) return null
      if (!this.studyCount) return null
      const totalMetrics = this.questionOptions.length
      const isBroad = this.studyCount >= 50 || (this.studyCount >= 10 && totalMetrics >= 50)
      if (!isBroad) return null
      return (
        `No metrics selected — this will export every available metric across ${this.studyCount} ` +
        'studies. Broad exports like this can take several minutes and may be delivered as CSV ' +
        'instead of XLSX. Performance improves the more specific you are with your filters, ' +
        'particularly the selected metrics.'
      )
    }
  },

  watch: {
    'filters.client'       () { this.onFiltersChanged() },
    'filters.category'     () { this.onFiltersChanged() },
    'filters.year'         () { this.onFiltersChanged() },
    'filters.country'      () { this.onFiltersChanged() },
    'filters.product_type' () { this.onFiltersChanged() }
  },

  async mounted () {
    this.restoreHistory()
    // Any historical job left in pending/running gets re-polled so we catch
    // up on its terminal state without blocking the user.
    for (const j of this.jobs) {
      if (j.status === 'pending' || j.status === 'running') this.startPoll(j.jobId)
    }
    await Promise.all([this.loadDropdownOptions(), this.loadQuestions(), this.loadMaxExportRows()])
  },

  beforeUnmount () {
    this.stopAllPolls()
    if (this.countDebounce) clearTimeout(this.countDebounce)
    if (this.questionsDebounce) clearTimeout(this.questionsDebounce)
    if (this.dropdownDebounce) clearTimeout(this.dropdownDebounce)
  },

  methods: {
    // Translate the current selections into smartsearch query format so the
    // dropdown options cascade — picking a client narrows category / year /
    // country / product_type to what actually co-occurs, same as the rest of
    // the app. year travels as a min/max range.
    _dropdownQuery () {
      const q = []
      if (this.filters.client.length) q.push({ field: 'client', value: [...this.filters.client], target: 'study' })
      if (this.filters.category.length) q.push({ field: 'category', value: [...this.filters.category], target: 'study' })
      if (this.filters.product_type.length) q.push({ field: 'product_type', value: [...this.filters.product_type], target: 'study' })
      if (this.filters.country.length) q.push({ field: 'country', value: [...this.filters.country], target: 'study' })
      const years = this.filters.year.map(Number).filter(Boolean)
      if (years.length) {
        q.push({
          field: 'year',
          value: String(Math.min(...years)),
          value_secondary: String(Math.max(...years)),
          target: 'study'
        })
      }
      return q
    },

    async loadDropdownOptions () {
      try {
        const query = this._dropdownQuery()
        const res = await http.post('/smartsearch/dropdown_options', { query, full_query: query })
        const groups = res.data?.fields || []
        const map = {}
        for (const group of groups) {
          for (const item of (group.data || [])) {
            if (item.field && Array.isArray(item.options)) {
              map[item.field] = item.options.map(o => (typeof o === 'object' ? o.value ?? o.label : o))
            }
          }
        }
        this.dropdownOptions = map
      } catch {
        // non-fatal
      }
    },

    // Build a payload of array-valued filters for POSTing to the backend.
    _filterPayload () {
      const out = {}
      if (this.filters.client.length) out.client = [...this.filters.client]
      if (this.filters.category.length) out.category = [...this.filters.category]
      if (this.filters.year.length) out.year = [...this.filters.year]
      if (this.filters.country.length) out.country = [...this.filters.country]
      if (this.filters.product_type.length) out.product_type = [...this.filters.product_type]
      return out
    },

    async loadQuestions () {
      this.questionsLoading = true
      try {
        // /questions is POST now: the body cleanly carries arrays without
        // needing querystring repeats or bracket conventions.
        const res = await http.post('/bulk-extract/questions', this._filterPayload())
        this.questionOptions = res.data?.data || []
        this.favoritesRegistry = res.data?.favorites || []
        // Intentionally NOT applying res.data.defaults here. Nothing is
        // selected by default; an empty question filter at submit time means
        // "export every metric", which matches what the user expects.
        this.questionsLoaded = true
      } catch {
        // non-fatal — leave questionsLoaded as-is so we don't grey
        // everything out just because availability lookup failed.
      } finally {
        this.questionsLoading = false
      }
    },

    onFiltersChanged () {
      this.debouncedCount()
      this.debouncedQuestions()
      this.debouncedDropdown()
    },

    debouncedDropdown () {
      if (this.dropdownDebounce) clearTimeout(this.dropdownDebounce)
      this.dropdownDebounce = setTimeout(() => this.loadDropdownOptions(), 500)
    },

    debouncedCount () {
      if (this.countDebounce) clearTimeout(this.countDebounce)
      if (!this.hasFilter) {
        this.studyCount = null
        this.resultsCount = null
        return
      }
      this.countDebounce = setTimeout(() => this.loadCount(), 500)
    },

    debouncedQuestions () {
      if (this.questionsDebounce) clearTimeout(this.questionsDebounce)
      this.questionsDebounce = setTimeout(() => this.loadQuestions(), 500)
    },

    isGroupExpanded (type) {
      return !!this.expandedGroups[type]
    },

    toggleGroup (type) {
      this.expandedGroups = {
        ...this.expandedGroups,
        [type]: !this.expandedGroups[type]
      }
    },

    formatGroupLabel (type) {
      if (!type) return ''
      // smartsearch returns labels like "Attribute: descriptives " — tidy up.
      return type.replace(/^Attribute:\s*/, '').replace(/_/g, ' ').trim()
        .replace(/\b\w/g, c => c.toUpperCase())
    },

    async loadCount () {
      if (!this.hasFilter) return
      this.countLoading = true
      try {
        const res = await http.post('/bulk-extract/count', this._filterPayload())
        this.studyCount = res.data?.data?.studyCount ?? null
        this.resultsCount = res.data?.data?.resultsCount ?? null
        if (res.data?.data?.maxExportRows != null) this.maxExportRows = res.data.data.maxExportRows
      } catch {
        this.studyCount = null
      } finally {
        this.countLoading = false
      }
    },

    // /count returns maxExportRows regardless of filters, but loadCount()
    // only fires once a filter is set. Prime it once on mount so the static
    // page description can state the cap immediately.
    async loadMaxExportRows () {
      try {
        const res = await http.post('/bulk-extract/count', {})
        if (res.data?.data?.maxExportRows != null) this.maxExportRows = res.data.data.maxExportRows
      } catch {
        // non-fatal — the warning/description just omit the number
      }
    },

    isQuestionSelected (label) {
      return this.filters.questions.includes(label)
    },

    toggleQuestion (label) {
      const idx = this.filters.questions.indexOf(label)
      if (idx === -1) {
        this.filters.questions = [...this.filters.questions, label]
      } else {
        this.filters.questions = this.filters.questions.filter(l => l !== label)
      }
    },

    clearAllQuestions () {
      this.filters.questions = []
    },

    // ── Job history -------------------------------------------------------

    restoreHistory () {
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) this.jobs = parsed.slice(0, this.maxHistory)
      } catch {
        // corrupt storage — clear silently
        try { window.localStorage.removeItem(this.storageKey) } catch { /* ignore */ }
      }
    },

    persistHistory () {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.jobs))
      } catch {
        // quota / disabled — non-fatal, history is just session-only then
      }
    },

    upsertJob (jobId, patch) {
      const idx = this.jobs.findIndex(j => j.jobId === jobId)
      if (idx === -1) {
        this.jobs = [{ jobId, ...patch }, ...this.jobs].slice(0, this.maxHistory)
      } else {
        const merged = { ...this.jobs[idx], ...patch }
        this.jobs = [...this.jobs.slice(0, idx), merged, ...this.jobs.slice(idx + 1)]
      }
      this.persistHistory()
    },

    removeJob (jobId) {
      this.stopPoll(jobId)
      this.jobs = this.jobs.filter(j => j.jobId !== jobId)
      this.persistHistory()
    },

    clearHistory () {
      this.stopAllPolls()
      this.jobs = []
      this.persistHistory()
    },

    runningStageText (j) {
      if (j.stage === 'processing' && j.tallRowCount) return `processing ${j.tallRowCount.toLocaleString()} rows`
      if (j.stage === 'pivoting') return 'reshaping results'
      if (j.stage === 'writing' && j.rowCount) return `writing ${j.rowCount.toLocaleString()} rows`
      if (j.rowsFetchedSoFar) return `${j.rowsFetchedSoFar.toLocaleString()} rows fetched so far`
      return 'running against Databricks'
    },

    formatTimestamp (iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toLocaleString()
      } catch {
        return iso
      }
    },

    // ── Run + poll --------------------------------------------------------

    async runExtract () {
      if (!this.hasFilter) return
      this.isSubmitting = true

      try {
        const payload = this._filterPayload()
        if (this.filters.questions?.length) payload.questions = this.filters.questions

        const res = await http.post('/bulk-extract/run', payload)
        const jobId = res.data?.data?.jobId
        if (!jobId) throw new Error('No jobId returned from server')

        this.upsertJob(jobId, {
          status: 'pending',
          filters: payload,
          createdAt: new Date().toISOString()
        })
        this.startPoll(jobId)
      } catch (err) {
        this.showAlert('danger', err?.response?.data?.message || err.message || 'Failed to launch extract job.')
      } finally {
        this.isSubmitting = false
      }
    },

    startPoll (jobId) {
      if (this.pollTimers[jobId]) return
      this.pollTimers[jobId] = setInterval(() => this.pollStatus(jobId), this.pollIntervalSec * 1000)
      this.pollStatus(jobId)
    },

    stopPoll (jobId) {
      const t = this.pollTimers[jobId]
      if (t) {
        clearInterval(t)
        delete this.pollTimers[jobId]
      }
    },

    stopAllPolls () {
      for (const jobId of Object.keys(this.pollTimers)) this.stopPoll(jobId)
    },

    async pollStatus (jobId) {
      try {
        const res = await http.get(`/bulk-extract/status/${jobId}`)
        const meta = res.data?.data
        if (meta) {
          this.upsertJob(jobId, meta)
          if (meta.status === 'done' || meta.status === 'error') {
            this.stopPoll(jobId)
          }
        }
      } catch {
        // keep polling silently
      }
    },

    async downloadFile (jobId) {
      const job = this.jobs.find(j => j.jobId === jobId)
      if (!job) return
      try {
        const res = await http.get(`/bulk-extract/download/${jobId}`, { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([res.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = job.downloadName || `iris_extract_${jobId.slice(0, 8)}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.showAlert('danger', 'Download failed. Please try again.')
      }
    },

    showAlert (type, text) {
      this.alert = { type, icon: type === 'danger' ? 'fa-exclamation-circle' : 'fa-info-circle', text, isVisible: true }
    },

    closeAlert () {
      this.alert.isVisible = false
    }
  }
}
</script>

<style scoped>
.page-header {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.description-copy {
  margin: 0 0 0.4rem 0;
}

.muted-label {
  font-size: 0.8em;
  color: #888;
}

/* Filter row: each filter fills its equal-width (col-xl) Bootstrap column so
   the five filters balance evenly across the row instead of the 5th wrapping
   onto its own line. */
.input-wrapper {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

/* Run Extract turns purple when the user can actually submit (button enabled:
   a filter is set and studies match). The global button:disabled style greys
   it out otherwise, so this only paints the submittable state. */
.search-actions .search-button:not(:disabled) {
  background-color: #A333C8 !important;
  border-color: #A333C8 !important;
  color: #fff !important;
}

.search-actions .search-button:not(:disabled):hover {
  background-color: #8F2BB0 !important;
  border-color: #8F2BB0 !important;
}

.chip-action-link {
  font-size: 0.8em;
  color: #5a8dee;
  text-decoration: none;
}
.chip-action-link:hover {
  text-decoration: underline;
}

.question-chips {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chip-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.chip-group-label {
  font-size: 0.72em;
  font-weight: 600;
  text-transform: uppercase;
  color: #aaa;
  letter-spacing: 0.04em;
  min-width: 80px;
  flex-shrink: 0;
}

.question-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1.5px solid #cdd4e0;
  background: #f8f9fc;
  color: #555;
  font-size: 0.82em;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.question-chip:hover {
  border-color: #5a8dee;
  color: #5a8dee;
  background: #eef3fd;
}

.question-chip.active {
  background: #5a8dee;
  border-color: #5a8dee;
  color: #fff;
  font-weight: 500;
}

.question-chip.disabled,
.question-chip.disabled:hover {
  background: #f1f3f7;
  border-color: #e3e7ee;
  color: #b6bbc4;
  cursor: not-allowed;
  text-decoration: line-through;
}

.favorites-row {
  padding: 8px 10px;
  background: #fafbff;
  border: 1px dashed #d6def0;
  border-radius: 8px;
}

.chip-group-collapsible {
  border-top: 1px solid #eef1f6;
  padding-top: 8px;
}

.chip-group-toggle {
  background: none;
  border: none;
  padding: 0;
  color: #555;
  font-size: 0.82em;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.chip-group-toggle:hover {
  color: #5a8dee;
}
.chip-group-toggle .fa {
  font-size: 0.7em;
  color: #aaa;
}

.chip-group-body {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding-left: 16px;
}

.job-row {
  border-top: 1px solid #eef1f6;
}
.job-row:first-child {
  border-top: none;
}
.job-status {
  width: 24px;
  text-align: center;
  font-size: 1.1em;
}
.job-name {
  font-size: 0.92em;
  line-height: 1.2;
  word-break: break-all;
}
.job-sub {
  font-size: 0.78em;
}
.job-actions .btn-link {
  padding: 0 4px;
}
</style>
